import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import PageSEO from '@/components/feature/PageSEO';
import { itemListToProductGraph } from '@/lib/seo';
import MRangeHero from './components/MRangeHero';
import ModularAdvantage from './components/ModularAdvantage';
import ProductModels from './components/ProductModels';
import ApplicationScenarios from './components/ApplicationScenarios';
import CallToAction from './components/CallToAction';
import Footer from '../home/components/Footer';

const mRangeJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'ItemList',
  name: 'M Series Modular Adventure Pods',
  description: 'Nexa Pods M Series — revolutionary flat-pack modular adventure cabins for remote locations. M5, M7, M9 models with manual assembly and all-terrain access.',
  numberOfItems: 3,
  itemListElement: [
    {
      '@type': 'ListItem',
      position: 1,
      item: {
        '@type': 'Product',
        name: 'M5 Modular Capsule',
        description: 'Perfect balance of space and portability with stunning panoramic windows for nature immersion. 26 m² living space.',
        brand: { '@type': 'Brand', name: 'Nexa Pods' },
        url: 'https://nexapods.com.au/m-range-details',
        image: 'https://nexapods.com.au/images/7f3649eeefc8.webp',
        offers: {
          '@type': 'Offer',
          priceCurrency: 'AUD',
          price: '59180',
          availability: 'https://schema.org/InStock',
        },
      },
    },
    {
      '@type': 'ListItem',
      position: 2,
      item: {
        '@type': 'Product',
        name: 'M7 Standard Configurations',
        description: 'Extended living space with complete amenities. Ideal for families or those seeking ultimate comfort in remote locations. 36 m².',
        brand: { '@type': 'Brand', name: 'Nexa Pods' },
        url: 'https://nexapods.com.au/m-range-details',
        image: 'https://nexapods.com.au/images/4f4d4413e7ef.webp',
        offers: {
          '@type': 'Offer',
          priceCurrency: 'AUD',
          price: '74140',
          availability: 'https://schema.org/InStock',
        },
      },
    },
    {
      '@type': 'ListItem',
      position: 3,
      item: {
        '@type': 'Product',
        name: 'M9 Standard Configurations',
        description: 'The ultimate modular solution offering maximum space and luxury for the most demanding remote installations. 38 m².',
        brand: { '@type': 'Brand', name: 'Nexa Pods' },
        url: 'https://nexapods.com.au/m-range-details',
        image: 'https://nexapods.com.au/images/014b41d04a33.webp',
        offers: {
          '@type': 'Offer',
          priceCurrency: 'AUD',
          price: '75460',
          availability: 'https://schema.org/InStock',
        },
      },
    },
  ],
};

const mRangeProductsJsonLd = itemListToProductGraph(mRangeJsonLd, {
  canonicalPath: '/m-range-details',
});

export default function MRangeDetailsPage() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [productsDropdownOpen, setProductsDropdownOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <>
      <PageSEO
        title="M Series Modular Adventure Pods | Nexa Pods Australia"
        description="Explore Nexa M Series modular pods for remote sites and locations with limited crane access, featuring transportable components assembled on site."
        canonicalPath="/m-range-details"
        keywords="M Series modular cabin, adventure pod Australia, flat-pack pod remote location, M5 M7 M9 modular pod Kew VIC"
        jsonLd={mRangeProductsJsonLd}
      />
      <div className="bg-gradient-to-b from-[#0A0A0A] via-[#0D1117] to-[#000000] text-white min-h-screen w-full overflow-x-hidden">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 w-full ${
        scrolled ? 'bg-black/80 backdrop-blur-xl border-b border-cyan-500/20' : 'bg-transparent'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6 flex items-center justify-between w-full">
          <Link to="/" className="flex items-center gap-3 flex-shrink-0">
            <img 
              src="/images/b58bd9e3723b.webp" 
              alt="Space Capsule Living" 
              className="h-16 sm:h-20 lg:h-28 w-auto"
            />
          </Link>
          
          {/* Mobile Menu Button */}
          <button 
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="lg:hidden w-10 h-10 flex items-center justify-center text-white cursor-pointer"
          >
            <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
          </button>

          {/* Desktop Menu */}
          <div className="hidden lg:flex items-center gap-8">
            <Link to="/" className="text-base text-gray-300 hover:text-gray-200 transition-colors whitespace-nowrap cursor-pointer">Home</Link>
            
            <div 
              className="relative"
              onMouseEnter={() => setProductsDropdownOpen(true)}
              onMouseLeave={() => setProductsDropdownOpen(false)}
            >
              <Link 
                to="/product-range"
                className="text-base text-gray-300 hover:text-gray-200 transition-colors whitespace-nowrap cursor-pointer flex items-center gap-2"
              >
                Products
                <i className={`ri-arrow-down-s-line text-lg transition-transform duration-300 ${productsDropdownOpen ? 'rotate-180' : ''}`}></i>
              </Link>
              
              {productsDropdownOpen && (
                <div className="absolute top-full left-0 pt-2 w-auto">
                  <div className="bg-black/90 backdrop-blur-xl border border-cyan-500/30 rounded-lg shadow-[0_0_20px_rgba(0,217,255,0.3)] overflow-hidden">
                    <Link 
                      to="/product-range" 
                      className="block px-6 py-3 text-base text-gray-300 hover:text-cyan-400 hover:bg-cyan-500/10 hover:shadow-[inset_0_0_10px_rgba(0,217,255,0.2)] transition-all cursor-pointer whitespace-nowrap"
                    >
                      All Products
                    </Link>
                    <Link 
                      to="/a-range-details" 
                      className="block px-6 py-3 text-base text-gray-300 hover:text-cyan-400 hover:bg-cyan-500/10 hover:shadow-[inset_0_0_10px_rgba(0,217,255,0.2)] transition-all cursor-pointer whitespace-nowrap"
                    >
                      A Series - Workplace Solution
                    </Link>
                    <Link 
                      to="/x-range-details" 
                      className="block px-6 py-3 text-base text-gray-300 hover:text-cyan-400 hover:bg-cyan-500/10 hover:shadow-[inset_0_0_10px_rgba(0,217,255,0.2)] transition-all cursor-pointer whitespace-nowrap"
                    >
                      X Series - Luxury Living
                    </Link>
                    <Link 
                      to="/m-range-details" 
                      className="block px-6 py-3 text-base text-cyan-400 bg-cyan-500/10 hover:bg-cyan-500/20 transition-all cursor-pointer whitespace-nowrap"
                    >
                      M Series - Modularize
                    </Link>
                    <Link 
                      to="/z-range-details" 
                      className="block px-6 py-3 text-base text-gray-400 hover:text-cyan-400 hover:bg-cyan-500/10 hover:shadow-[inset_0_0_10px_rgba(0,217,255,0.2)] transition-all cursor-pointer whitespace-nowrap"
                    >
                      Z Series - Coming Soon
                    </Link>
                  </div>
                </div>
              )}
            </div>

            <a href="#models" className="text-base text-gray-300 hover:text-gray-200 transition-colors whitespace-nowrap cursor-pointer">Models</a>
            <a href="#applications" className="text-base text-gray-300 hover:text-gray-200 transition-colors whitespace-nowrap cursor-pointer">Applications</a>
            <Link to="/contact" className="px-6 py-2.5 bg-cyan-500 text-black text-base font-medium rounded-full hover:bg-cyan-400 transition-all hover:shadow-[0_0_20px_rgba(0,217,255,0.5)] whitespace-nowrap cursor-pointer">
              Contact Us
            </Link>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden bg-black/95 backdrop-blur-xl border-t border-cyan-500/20 w-full">
            <div className="px-4 py-6 space-y-4 w-full">
              <Link to="/" onClick={() => setMobileMenuOpen(false)} className="block text-base text-gray-300 hover:text-cyan-400 transition-colors py-2 cursor-pointer">Home</Link>
              
              <div>
                <button 
                  onClick={() => setProductsDropdownOpen(!productsDropdownOpen)}
                  className="w-full flex items-center justify-between text-base text-gray-300 hover:text-cyan-400 transition-colors py-2 cursor-pointer"
                >
                  Products
                  <i className={`ri-arrow-down-s-line text-lg transition-transform duration-300 ${productsDropdownOpen ? 'rotate-180' : ''}`}></i>
                </button>
                
                {productsDropdownOpen && (
                  <div className="mt-2 ml-4 space-y-2 bg-black/90 border border-cyan-500/30 rounded-lg shadow-[0_0_15px_rgba(0,217,255,0.2)] overflow-hidden">
                    <Link 
                      to="/product-range" 
                      onClick={() => setMobileMenuOpen(false)}
                      className="block px-4 py-2 text-sm text-gray-300 hover:text-cyan-400 hover:bg-cyan-500/10 transition-all cursor-pointer whitespace-nowrap"
                    >
                      All Products
                    </Link>
                    <Link 
                      to="/a-range-details" 
                      onClick={() => setMobileMenuOpen(false)}
                      className="block px-4 py-2 text-sm text-gray-300 hover:text-cyan-400 hover:bg-cyan-500/10 transition-all cursor-pointer whitespace-nowrap"
                    >
                      A Series - Workplace Solution
                    </Link>
                    <Link 
                      to="/x-range-details" 
                      onClick={() => setMobileMenuOpen(false)}
                      className="block px-4 py-2 text-sm text-gray-300 hover:text-cyan-400 hover:bg-cyan-500/10 transition-all cursor-pointer whitespace-nowrap"
                    >
                      X Series - Luxury Living
                    </Link>
                    <Link 
                      to="/m-range-details" 
                      onClick={() => setMobileMenuOpen(false)}
                      className="block px-4 py-2 text-sm text-cyan-400 bg-cyan-500/10 transition-all cursor-pointer whitespace-nowrap"
                    >
                      M Series - Modularize
                    </Link>
                    <Link 
                      to="/z-range-details" 
                      onClick={() => setMobileMenuOpen(false)}
                      className="block px-4 py-2 text-sm text-gray-400 hover:text-cyan-400 hover:bg-cyan-500/10 transition-all cursor-pointer whitespace-nowrap"
                    >
                      Z Series - Coming Soon
                    </Link>
                  </div>
                )}
              </div>

              <a href="#models" onClick={() => setMobileMenuOpen(false)} className="block text-base text-gray-300 hover:text-cyan-400 transition-colors py-2 cursor-pointer">Models</a>
              <a href="#applications" onClick={() => setMobileMenuOpen(false)} className="block text-base text-gray-300 hover:text-cyan-400 transition-colors py-2 cursor-pointer">Applications</a>
              <Link to="/contact" className="block w-full text-center px-6 py-2.5 bg-cyan-500 text-black text-base font-medium rounded-full hover:bg-cyan-400 transition-all whitespace-nowrap cursor-pointer">
                Contact Us
              </Link>
            </div>
          </div>
        )}
      </nav>

      {/* Page Content */}
      <MRangeHero />
      <section className="border-y border-cyan-500/15 bg-cyan-500/[0.04]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 py-10 grid md:grid-cols-2 gap-8 items-center">
          <div>
            <p className="text-xs tracking-[0.24em] uppercase text-cyan-400 mb-3">Choose the right M5</p>
            <h2 className="text-2xl sm:text-3xl font-light">Residential and adventure are separate configurations.</h2>
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <Link to="/m5-residential-granny-flat" className="px-5 py-3 rounded-full bg-cyan-500 text-black text-center font-medium hover:bg-cyan-400">M5 Residential</Link>
            <Link to="/glamping-pods-australia" className="px-5 py-3 rounded-full border border-cyan-500/40 text-cyan-300 text-center hover:border-cyan-300">M5 Adventure</Link>
          </div>
        </div>
      </section>
      <ModularAdvantage />
      <ProductModels />
      <ApplicationScenarios />
      <CallToAction />
      <Footer />
    </div>
    </>
  );
}
